<template>
  <div>
    <v-form>
      <h2 class="mb-4">Gravimetric Analysis of a Metal Carbonate - Data Sheet</h2>

      <p class="mb-3">a) What is the concentration of your stock solution of Blue #1?</p>

      <calculation-input
        v-model="inputs.concStockSoln"
        prepend-text="$\text{[Stock]}:$"
        append-text="$\text{M}$"
        class="mb-5"
        :disabled="!allowEditing"
      />

      <p class="mb-2">b) Please enter your experimental data in the table below:</p>

      <v-simple-table class="mb-6" style="max-width: 591px">
        <thead>
          <tr>
            <td style="text-align: center; font-weight: bold; width: 21%" class="pl-2">
              Volume of stock solution, Blue #1 (mL)
            </td>
            <td style="text-align: center; font-weight: bold; width: 21%">
              Volume of distilled water (mL)
            </td>
            <td style="text-align: center; font-weight: bold; width: 29%">[Blue #1] (M)</td>
            <td style="text-align: center; font-weight: bold; width: 29%">Absorbance</td>
          </tr>
        </thead>
        <tbody>
          <tr v-for="property in properties" :key="property.text">
            <td class="px-1 py-2" style="text-align: center">
              <stemble-latex :content="property.volStock" />
            </td>
            <td class="px-1 py-2" style="text-align: center">
              <stemble-latex :content="property.volDistilled" />
            </td>
            <td class="centered-input px-2 py-2">
              <calculation-input v-model="inputs[property.input1]" :disabled="!allowEditing" />
            </td>
            <td class="centered-input px-2 py-2">
              <calculation-input v-model="inputs[property.input2]" :disabled="!allowEditing" />
            </td>
          </tr>
        </tbody>
      </v-simple-table>

      <p class="mb-3">
        c) Please upload your Beer's Law plot using the input field below. Be sure to double check
        the following before uploading:
      </p>

      <ul class="pl-10 mb-4">
        <li>The axes are labeled (with units for concentration)</li>
        <li>It has an informative title that also includes your name and section number</li>
        <li>It has a linear line of best fit</li>
        <li>The equation for the line of best fit is shown on the graph</li>
      </ul>

      <file-upload-ui-component
        style="width: 100%"
        label="Click here to select a file to upload"
        :max-number-of-files="1"
        :max-file-size="20"
        accepted-file-extensions=".png, .jpg, .jpeg, .webp"
        @upload:attachments="attachments = $event"
        class="mb-6"
      />

      <p class="mb-3">
        d) What were the slope and y-intercept from the equation of the line of best fit from your
        Beer's Law plot? Express your answers to 3 significant figures.
      </p>

      <calculation-input
        v-model="inputs.slope"
        prepend-text="$\text{Slope}:$"
        append-text="$\text{M}^{-1}$"
        class="mb-3"
        :disabled="!allowEditing"
      />

      <calculation-input
        v-model="inputs.yInt"
        prepend-text="$\text{y-int}:$"
        class="mb-5"
        :disabled="!allowEditing"
      />

      <p class="mb-6">
        <stemble-latex :content="beersLawEqn" />
      </p>

      <p class="mb-2">e) What is the absorbance of the sports drink solution?</p>

      <calculation-input
        v-model="inputs.absSportsDrink"
        prepend-text="$\text{Abs}:$"
        class="mb-5"
        :disabled="!allowEditing"
      />

      <p class="mb-2">f) What is the concentration of Blue #1 in the Gatorade drink?</p>

      <calculation-input
        v-model="inputs.concSportsDrink"
        prepend-text="$\text{[Blue }\#1\text{]}:$"
        append-text="$\text{M}$"
        class="mb-5"
        :disabled="!allowEditing"
      />
    </v-form>
  </div>
</template>

<script>
import DynamicQuestionMixin from '../../mixins/dynamic-question';
import StembleLatex from '@/tasks/components/StembleLatex.vue';
import CalculationInput from '../inputs/CalculationInput.vue';
import ChemicalLatex from '@/tasks/components/displayers/ChemicalLatex.vue';
import FileUploadUiComponent from '@/tasks/components/task-ui-components/FileUploadUiComponent.vue';

export default {
  name: 'OleMissGatorade_ReportSheet',
  components: {FileUploadUiComponent, ChemicalLatex, StembleLatex, CalculationInput},
  mixins: [DynamicQuestionMixin()],
  data() {
    return {
      inputs: {
        concStockSoln: null,

        concDye1: null,
        concDye2: null,
        concDye3: null,
        concDye4: null,
        concDye5: null,
        concDye6: null,
        concDye7: null,
        concDye8: null,
        concDye9: null,
        concDye10: null,

        abs1: null,
        abs2: null,
        abs3: null,
        abs4: null,
        abs5: null,
        abs6: null,
        abs7: null,
        abs8: null,
        abs9: null,
        abs10: null,

        slope: null,
        yInt: null,

        absSportsDrink: null,
        concSportsDrink: null,
      },
      attachments: [],
      properties: [
        {
          text: 'Solution 1',
          volStock: '10',
          volDistilled: '0',
          input1: 'concDye1',
          input2: 'abs1',
        },
        {
          text: 'Solution 2',
          volStock: '9',
          volDistilled: '1',
          input1: 'concDye2',
          input2: 'abs2',
        },
        {
          text: 'Solution 3',
          volStock: '8',
          volDistilled: '2',
          input1: 'concDye3',
          input2: 'abs3',
        },
        {
          text: 'Solution 4',
          volStock: '7',
          volDistilled: '3',
          input1: 'concDye4',
          input2: 'abs4',
        },
        {
          text: 'Solution 5',
          volStock: '6',
          volDistilled: '4',
          input1: 'concDye5',
          input2: 'abs5',
        },
        {
          text: 'Solution 6',
          volStock: '5',
          volDistilled: '5',
          input1: 'concDye6',
          input2: 'abs6',
        },
        {
          text: 'Solution 7',
          volStock: '4',
          volDistilled: '6',
          input1: 'concDye7',
          input2: 'abs7',
        },
        {
          text: 'Solution 8',
          volStock: '3',
          volDistilled: '7',
          input1: 'concDye8',
          input2: 'abs8',
        },
        {
          text: 'Solution 9',
          volStock: '2',
          volDistilled: '8',
          input1: 'concDye9',
          input2: 'abs9',
        },
        {
          text: 'Solution 10',
          volStock: '1',
          volDistilled: '9',
          input1: 'concDye10',
          input2: 'abs10',
        },
      ],
      options2: [
        {text: 'i) Mass of unknown carbonate', input: 'massUnknownCarbonate'},
        {text: 'ii) Mass of $\\ce{CaCO3}$ precipitate', input: 'massPrecipitate'},
        {text: 'iii) Moles of $\\ce{CaCO3}$', input: 'molCaCO3'},
        {text: 'iv) Molar mass of unknown carbonate', input: 'mwMetalCarbonate'},
        {text: 'v) Molar mass of the metal in the carbonate', input: 'mwMetal'},
      ],
      options3: [
        {text: 'Li', value: 'Li'},
        {text: 'Na', value: 'Na'},
        {text: 'K', value: 'K'},
        {text: 'Rb', value: 'Rb'},
        {text: 'Cs', value: 'Cs'},
      ],
    };
  },
  computed: {
    beersLawEqn() {
      if (
        (this.inputs.slope === null || this.inputs.slope === '') &&
        (this.inputs.yInt === null || this.inputs.yInt === '')
      ) {
        return '$\\textbf{Equation: }$ Please enter your slope and y-intercept values';
      } else if (this.inputs.slope === null || this.inputs.slope === '') {
        return '$\\textbf{Equation: }$ Please enter your slope value';
      } else if (this.inputs.yInt === null || this.inputs.yInt === '') {
        return '$\\textbf{Equation: }$ Please enter your y-intercept value';
      } else {
        if (parseFloat(this.inputs.yInt) < 0) {
          return `$\\textbf{Equation: }y=${this.inputs.slope}\\,x ${this.inputs.yInt}$`;
        } else {
          return `$\\textbf{Equation: }y=${this.inputs.slope}\\,x + ${this.inputs.yInt}$`;
        }
      }
    },
  },
};
</script>
